import { useMutation } from '@tanstack/react-query'
import { ArrowDownIcon, Button, Spinner } from '@viapeople/components'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { trpc, trpcClient } from '../../../utils/trpc'

interface PdfDownloadButtonProps {
	/** Task ID for backend PDF generation. When undefined, falls back to browser print. */
	taskId?: string
	variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive'
	size?: 'small' | 'medium' | 'full'
	className?: string
	label?: string
}

const POLL_INTERVAL_MS = 2_000
const MAX_POLL_ATTEMPTS = 150 // 5 minutes at 2s intervals

/**
 * PDF download button that uses backend generation when a taskId is available.
 * Falls back to browser print dialog (window.print()) when no taskId is provided,
 * e.g. on viewer/preview pages that aggregate data across evaluations.
 */
export const PdfDownloadButton: React.FC<PdfDownloadButtonProps> = ({
	taskId,
	variant = 'secondary',
	size = 'small',
	className,
	label = 'Download PDF',
}) => {
	const { showToast } = useToast()
	const [jobId, setJobId] = useState<string | null>(null)
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const pollCountRef = useRef(0)
	const isMountedRef = useRef(true)

	// Track mounted state to avoid state updates after unmount
	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
		}
	}, [])

	// Clean up polling on unmount
	useEffect(() => {
		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current)
				pollingRef.current = null
			}
		}
	}, [])

	const generatePdfMutation = useMutation({
		...trpc.client.reports.generateTaskPdf.mutationOptions(),
	})

	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current)
			pollingRef.current = null
		}
		pollCountRef.current = 0
		if (isMountedRef.current) {
			setJobId(null)
		}
	}, [])

	// Poll for job completion
	useEffect(() => {
		if (!jobId) return

		pollCountRef.current = 0

		pollingRef.current = setInterval(async () => {
			pollCountRef.current += 1

			if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
				stopPolling()
				if (isMountedRef.current) {
					showToast('PDF generation timed out. Please try again.', 'error')
				}
				return
			}

			try {
				const status = await trpcClient.client.reports.getBulkPdfJobStatus.query({ jobId })

				if (!isMountedRef.current) return

				if (status.status === 'completed' && status.downloadUrl) {
					stopPolling()

					// Presigned URL has Content-Disposition: attachment, so browser will download
					window.location.href = status.downloadUrl

					showToast('PDF downloaded successfully')
				} else if (status.status === 'failed') {
					stopPolling()
					showToast(`PDF generation failed: ${status.errorMessage || 'Unknown error'}`, 'error')
				}
				// 'pending' and 'processing' — keep polling
			} catch {
				// Silently retry on next interval — transient network errors
			}
		}, POLL_INTERVAL_MS)

		return () => {
			if (pollingRef.current) {
				clearInterval(pollingRef.current)
				pollingRef.current = null
			}
		}
	}, [jobId, stopPolling, showToast])

	const handleClick = useCallback(() => {
		if (!taskId) {
			// Fallback: browser print for pages without a taskId (viewer/preview)
			window.print()
			return
		}

		if (jobId || generatePdfMutation.isPending) return // Prevent double-clicks

		generatePdfMutation.mutate(
			{ taskId },
			{
				onSuccess: (data) => {
					if (isMountedRef.current) {
						setJobId(data.jobId)
					}
				},
				onError: (error) => {
					if (isMountedRef.current) {
						showToast(`Failed to start PDF generation: ${error.message}`, 'error')
					}
				},
			},
		)
	}, [taskId, jobId, generatePdfMutation, showToast])

	const isGenerating = generatePdfMutation.isPending || !!jobId

	// Contextual label: show fallback hint when no backend generation available
	const fallbackLabel = !taskId ? 'Print / Save as PDF' : label

	return (
		<Button
			data-testid='pdf-download-btn'
			onClick={handleClick}
			variant={variant}
			size={size}
			className={className}
			disabled={isGenerating}
			icon={isGenerating ? undefined : ArrowDownIcon}
			iconPosition='left'>
			{isGenerating ? (
				<>
					<Spinner className='w-3 h-3 mr-1' />
					Generating...
				</>
			) : (
				fallbackLabel
			)}
		</Button>
	)
}
